import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Package, MessageSquare, Mail, Phone, MapPin, Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { contactInfo } from '@/data/index';
import { InquiryForm } from '@/components/InquiryForm';

declare global {
  interface Window {
    Tawk_API?: { toggle: () => void; maximize: () => void; };
  }
}

export default function Contact() {
  const [sampleModalOpen, setSampleModalOpen] = useState(false);

  const openTawkChat = () => {
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
    }
  };

  return (
    <div className="min-h-screen bg-background py-24">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-primary" />
            <span className="font-mono text-xs font-bold tracking-widest text-primary uppercase">
              CONTACT US
            </span>
            <div className="h-px w-12 bg-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            联系我们
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            专业技术团队为您提供选材建议、定制咨询和样品申请服务
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card-industrial p-8"
          >
            <div className="mb-6">
              <span className="inline-flex items-center font-mono text-xs font-bold tracking-widest text-primary uppercase bg-primary/10 border border-primary/20 px-2 py-1 rounded-sm">
                ONLINE INQUIRY
              </span>
              <h2 className="text-2xl font-bold mt-3 mb-1">在线询价</h2>
              <p className="text-sm text-muted-foreground">
                填写需求信息，技术工程师将在1个工作日内回复
              </p>
            </div>
            <InquiryForm />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            <div className="card-industrial p-8">
              <h2 className="text-2xl font-bold mb-6">联系方式</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-2">北京总部</h3>
                    <p className="text-muted-foreground">{contactInfo.address.hq}</p>
                    <p className="text-sm text-muted-foreground mt-1">{contactInfo.companyName}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-2">制造基地</h3>
                    <p className="text-muted-foreground">{contactInfo.address.factory}</p>
                  </div>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">联系电话</h3>
                    <p className="text-muted-foreground font-mono">{contactInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">电子邮箱</h3>
                    <p className="text-muted-foreground font-mono">{contactInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">微信</h3>
                    <p className="text-muted-foreground font-mono">{contactInfo.wechat}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-industrial p-6 bg-primary/5 border-primary/20">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-5 h-5 text-primary" />
                <h3 className="font-bold">工作时间</h3>
              </div>
              <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">周一至周五</span>
                    <span className="font-mono">08:30 - 17:30</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">周六</span>
                    <span className="font-mono">09:00 - 12:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">周日及节假日</span>
                    <span className="text-muted-foreground">休息</span>
                  </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                紧急技术支持请直接拨打手机：13426031477
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <div className="card-industrial p-6 hover:border-primary/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold mb-2">技术资料下载</h3>
            <p className="text-sm text-muted-foreground mb-4">
              下载各产品系列的技术数据表、安全数据表及认证证书
            </p>
            <a
              href="https://nutbwknubyxyuaognyyu.supabase.co/storage/v1/object/public/documents/poly-prime.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              className="block w-full"
            >
              <Button variant="outline" className="w-full btn-outline-industrial">
                下载资料
              </Button>
            </a>
          </div>

          <div className="card-industrial p-6 hover:border-primary/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Package className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold mb-2">申请样品</h3>
            <p className="text-sm text-muted-foreground mb-4">
              免费提供标准规格样品，用于材料评估和测试验证
            </p>
            <Button
              variant="outline"
              className="w-full btn-outline-industrial"
              onClick={() => setSampleModalOpen(true)}
            >
              申请样品
            </Button>
          </div>

          <div className="card-industrial p-6 hover:border-primary/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold mb-2">技术咨询</h3>
            <p className="text-sm text-muted-foreground mb-4">
              提供材料选型建议、定制配方开发和工程技术支持
            </p>
            <Button
              variant="outline"
              className="w-full btn-outline-industrial"
              onClick={openTawkChat}
            >
              立即咨询
            </Button>
          </div>
        </motion.div>
      </div>

      {/* 申请样品 Modal */}
      <AnimatePresence>
        {sampleModalOpen && (
          <>
            <motion.div
              key="sample-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              onClick={() => setSampleModalOpen(false)}
            />
            <motion.div
              key="sample-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            >
              <div
                className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto bg-background border border-border rounded-md shadow-2xl pointer-events-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-background border-b border-border">
                  <div>
                    <span className="inline-flex items-center font-mono text-xs font-bold tracking-widest text-primary uppercase bg-primary/10 border border-primary/20 px-2 py-1 rounded-sm mb-2">
                      SAMPLE REQUEST
                    </span>
                    <h2 className="text-xl font-bold">申请样品</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      填写样品信息，我们将在2个工作日内安排寄送
                    </p>
                  </div>
                  <button
                    onClick={() => setSampleModalOpen(false)}
                    className="ml-4 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="px-6 py-6">
                  <InquiryForm
                    mode="sample"
                    onSuccess={() => {
                      setTimeout(() => setSampleModalOpen(false), 3000);
                    }}
                  />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}